import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Building2, Cpu, Image, Upload, X, Loader2, MapPin, Calendar, User, Briefcase, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ManageProjects = () => {
  const { language } = useLanguage();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const imageInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image_url: '',
    category: 'construction',
    is_featured: false,
    is_active: true,
    client: '',
    location: '',
    year: '',
    scope: '',
    detail_description: '',
    gallery_images: []
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects?active_only=false`);
      setProjects(response.data);
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to fetch projects' : 'Gagal mengambil proyek');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (file, type = 'main') => {
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(language === 'en' ? 'Invalid file type. Please upload an image.' : 'Tipe file tidak valid.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(language === 'en' ? 'File too large. Maximum 5MB.' : 'File terlalu besar. Maksimal 5MB.');
      return;
    }

    if (type === 'main') {
      setUploading(true);
    } else {
      setUploadingGallery(true);
    }

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Use the relative URL since uploads are in frontend public folder
      const fileUrl = response.data.url;

      if (type === 'main') {
        setFormData(prev => ({ ...prev, image_url: fileUrl }));
      } else {
        setFormData(prev => ({ 
          ...prev, 
          gallery_images: [...prev.gallery_images, fileUrl] 
        }));
      }
      
      toast.success(language === 'en' ? 'Image uploaded!' : 'Gambar diunggah!');
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'en' ? 'Upload failed' : 'Gagal mengunggah'));
    } finally {
      setUploading(false);
      setUploadingGallery(false);
    }
  };

  const removeGalleryImage = (index) => {
    setFormData(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== index)
    }));
  };

  const openCreateDialog = () => {
    setEditingProject(null);
    setFormData({
      title: '',
      description: '',
      image_url: '',
      category: 'construction',
      is_featured: false,
      is_active: true,
      client: '',
      location: '',
      year: '',
      scope: '',
      detail_description: '',
      gallery_images: []
    });
    setDialogOpen(true);
  };

  const openEditDialog = (project) => {
    setEditingProject(project);
    setFormData({
      title: project.title,
      description: project.description,
      image_url: project.image_url || '',
      category: project.category,
      is_featured: project.is_featured,
      is_active: project.is_active,
      client: project.client || '',
      location: project.location || '',
      year: project.year || '',
      scope: project.scope || '',
      detail_description: project.detail_description || '',
      gallery_images: project.gallery_images || []
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingProject) {
        await axios.put(`${API}/projects/${editingProject.id}`, formData);
        toast.success(language === 'en' ? 'Project updated!' : 'Proyek diperbarui!');
      } else {
        await axios.post(`${API}/projects`, formData);
        toast.success(language === 'en' ? 'Project created!' : 'Proyek dibuat!');
      }
      setDialogOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'en' ? 'Delete this project?' : 'Hapus proyek ini?')) return;
    
    try {
      await axios.delete(`${API}/projects/${id}`);
      toast.success(language === 'en' ? 'Project deleted!' : 'Proyek dihapus!');
      fetchProjects();
    } catch (error) {
      toast.error(language === 'en' ? 'Failed to delete' : 'Gagal menghapus');
    }
  };

  if (loading) {
    return (
      <AdminLayout title={language === 'en' ? 'Manage Projects' : 'Kelola Proyek'}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </AdminLayout>
    );
  }

  const constructionProjects = projects.filter(p => p.category === 'construction');
  const itProjects = projects.filter(p => p.category === 'it');

  return (
    <AdminLayout title={language === 'en' ? 'Manage Projects' : 'Kelola Proyek'}>
      <div className="space-y-8" data-testid="manage-projects-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <p className="text-stone-600">
            {language === 'en' 
              ? 'Add, edit, or remove projects displayed on the Projects page.'
              : 'Tambah, edit, atau hapus proyek yang ditampilkan di halaman Proyek.'}
          </p>
          <Button onClick={openCreateDialog} className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-project-btn">
            <Plus className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Add Project' : 'Tambah Proyek'}
          </Button>
        </div>

        {/* Construction Projects */}
        <Card data-testid="construction-projects-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-emerald-500" />
              {language === 'en' ? 'Construction Projects' : 'Proyek Konstruksi'}
              <span className="text-sm font-normal text-stone-500">({constructionProjects.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {constructionProjects.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {constructionProjects.map((project, index) => (
                  <div 
                    key={project.id}
                    className="flex gap-4 p-4 bg-stone-50 rounded-lg"
                    data-testid={`construction-project-${index}`}
                  >
                    {project.image_url ? (
                      <img 
                        src={project.image_url} 
                        alt={project.title}
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-stone-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-8 h-8 text-stone-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-stone-900 truncate">{project.title}</h4>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(project)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(project.id)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-stone-500 line-clamp-2 mt-1">{project.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          project.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'
                        }`}>
                          {project.is_active ? (language === 'en' ? 'Active' : 'Aktif') : (language === 'en' ? 'Inactive' : 'Nonaktif')}
                        </span>
                        {project.is_featured && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                            {language === 'en' ? 'Featured' : 'Unggulan'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-stone-500 py-8">
                {language === 'en' ? 'No construction projects yet' : 'Belum ada proyek konstruksi'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* IT Projects */}
        <Card data-testid="it-projects-section">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cpu className="w-5 h-5 text-sky-500" />
              {language === 'en' ? 'IT & Digital Projects' : 'Proyek IT & Digital'}
              <span className="text-sm font-normal text-stone-500">({itProjects.length})</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {itProjects.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {itProjects.map((project, index) => (
                  <div 
                    key={project.id}
                    className="flex gap-4 p-4 bg-stone-50 rounded-lg"
                    data-testid={`it-project-${index}`}
                  >
                    {project.image_url ? (
                      <img 
                        src={project.image_url} 
                        alt={project.title}
                        className="w-24 h-24 object-cover rounded-lg flex-shrink-0"
                      />
                    ) : (
                      <div className="w-24 h-24 bg-stone-200 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Cpu className="w-8 h-8 text-stone-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="font-medium text-stone-900 truncate">{project.title}</h4>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" onClick={() => openEditDialog(project)}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(project.id)} className="text-red-600 hover:text-red-700">
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-stone-500 line-clamp-2 mt-1">{project.description}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          project.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-stone-200 text-stone-600'
                        }`}>
                          {project.is_active ? (language === 'en' ? 'Active' : 'Aktif') : (language === 'en' ? 'Inactive' : 'Nonaktif')}
                        </span>
                        {project.is_featured && (
                          <span className="px-2 py-0.5 text-xs rounded-full bg-amber-100 text-amber-700">
                            {language === 'en' ? 'Featured' : 'Unggulan'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-stone-500 py-8">
                {language === 'en' ? 'No IT projects yet' : 'Belum ada proyek IT'}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Project Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="project-dialog">
          <DialogHeader>
            <DialogTitle>
              {editingProject 
                ? (language === 'en' ? 'Edit Project' : 'Edit Proyek')
                : (language === 'en' ? 'Add Project' : 'Tambah Proyek')
              }
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                {language === 'en' ? 'Basic Information' : 'Informasi Dasar'}
              </h3>
              
              <div>
                <Label>{language === 'en' ? 'Project Title' : 'Judul Proyek'} *</Label>
                <Input
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder={language === 'en' ? 'e.g., Green Office Tower BSD' : 'contoh: Menara Kantor Hijau BSD'}
                  className="mt-1"
                  data-testid="project-title-input"
                />
              </div>

              <div>
                <Label>{language === 'en' ? 'Short Description' : 'Deskripsi Singkat'} *</Label>
                <Textarea
                  required
                  rows={2}
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder={language === 'en' ? 'Brief project summary...' : 'Ringkasan proyek singkat...'}
                  className="mt-1"
                  data-testid="project-description-input"
                />
              </div>

              <div>
                <Label>{language === 'en' ? 'Category' : 'Kategori'} *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({...formData, category: v})}>
                  <SelectTrigger className="mt-1" data-testid="project-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="construction">
                      <span className="flex items-center gap-2">
                        <Building2 className="w-4 h-4" />
                        {language === 'en' ? 'Construction' : 'Konstruksi'}
                      </span>
                    </SelectItem>
                    <SelectItem value="it">
                      <span className="flex items-center gap-2">
                        <Cpu className="w-4 h-4" />
                        {language === 'en' ? 'IT & Digital' : 'IT & Digital'}
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Project Details */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                <Briefcase className="w-4 h-4" />
                {language === 'en' ? 'Project Details' : 'Detail Proyek'}
              </h3>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-1">
                    <User className="w-3 h-3" />
                    {language === 'en' ? 'Client' : 'Klien'}
                  </Label>
                  <Input
                    value={formData.client}
                    onChange={(e) => setFormData({...formData, client: e.target.value})}
                    placeholder={language === 'en' ? 'Client name' : 'Nama klien'}
                    className="mt-1"
                    data-testid="project-client-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {language === 'en' ? 'Location' : 'Lokasi'}
                  </Label>
                  <Input
                    value={formData.location}
                    onChange={(e) => setFormData({...formData, location: e.target.value})}
                    placeholder={language === 'en' ? 'Project location' : 'Lokasi proyek'}
                    className="mt-1"
                    data-testid="project-location-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {language === 'en' ? 'Year' : 'Tahun'}
                  </Label>
                  <Input
                    value={formData.year}
                    onChange={(e) => setFormData({...formData, year: e.target.value})}
                    placeholder="2024"
                    className="mt-1"
                    data-testid="project-year-input"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    {language === 'en' ? 'Scope' : 'Lingkup'}
                  </Label>
                  <Input
                    value={formData.scope}
                    onChange={(e) => setFormData({...formData, scope: e.target.value})}
                    placeholder={language === 'en' ? 'e.g., Design & Build' : 'contoh: Desain & Konstruksi'}
                    className="mt-1"
                    data-testid="project-scope-input"
                  />
                </div>
              </div>

              <div>
                <Label>{language === 'en' ? 'Detailed Description' : 'Deskripsi Detail'}</Label>
                <Textarea
                  rows={4}
                  value={formData.detail_description}
                  onChange={(e) => setFormData({...formData, detail_description: e.target.value})}
                  placeholder={language === 'en' ? 'Full project details, achievements, technologies used...' : 'Detail lengkap proyek, pencapaian, teknologi yang digunakan...'}
                  className="mt-1"
                  data-testid="project-detail-input"
                />
                <p className="text-xs text-stone-500 mt-1">
                  {language === 'en' ? 'This will be shown in the project detail modal' : 'Ini akan ditampilkan di modal detail proyek'}
                </p>
              </div>
            </div>

            {/* Images */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="font-semibold text-stone-900 flex items-center gap-2">
                <Image className="w-4 h-4" />
                {language === 'en' ? 'Images' : 'Gambar'}
              </h3>

              {/* Main Image */}
              <div>
                <Label>{language === 'en' ? 'Main Image' : 'Gambar Utama'}</Label>
                <div className="mt-2 space-y-2">
                  <div className="flex gap-2">
                    <Input
                      value={formData.image_url}
                      onChange={(e) => setFormData({...formData, image_url: e.target.value})}
                      placeholder="https://example.com/image.jpg"
                      className="flex-1"
                      data-testid="project-image-input"
                    />
                    <input
                      type="file"
                      ref={imageInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'main')}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => imageInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="upload-main-image-btn"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    </Button>
                  </div>
                  {formData.image_url && (
                    <div className="relative inline-block">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-full h-32 object-cover rounded-lg"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="absolute top-2 right-2 bg-white/80 hover:bg-white"
                        onClick={() => setFormData({...formData, image_url: ''})}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>

              {/* Gallery Images */}
              <div>
                <Label>{language === 'en' ? 'Gallery Images' : 'Galeri Gambar'}</Label>
                <div className="mt-2 space-y-3">
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={galleryInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={(e) => handleFileUpload(e.target.files[0], 'gallery')}
                    />
                    <Button 
                      type="button" 
                      variant="outline"
                      onClick={() => galleryInputRef.current?.click()}
                      disabled={uploadingGallery}
                      className="w-full"
                      data-testid="upload-gallery-btn"
                    >
                      {uploadingGallery ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Upload className="w-4 h-4 mr-2" />
                      )}
                      {language === 'en' ? 'Add Gallery Image' : 'Tambah Gambar Galeri'}
                    </Button>
                  </div>
                  {formData.gallery_images.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {formData.gallery_images.map((img, idx) => (
                        <div key={idx} className="relative group">
                          <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-20 object-cover rounded-lg" />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="absolute top-1 right-1 bg-white/80 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 p-0"
                            onClick={() => removeGalleryImage(idx)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Toggles */}
            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_featured}
                  onCheckedChange={(checked) => setFormData({...formData, is_featured: checked})}
                  data-testid="project-featured-toggle"
                />
                <Label>{language === 'en' ? 'Featured Project' : 'Proyek Unggulan'}</Label>
              </div>
              <div className="flex items-center gap-3">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({...formData, is_active: checked})}
                  data-testid="project-active-toggle"
                />
                <Label>{language === 'en' ? 'Active' : 'Aktif'}</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {language === 'en' ? 'Cancel' : 'Batal'}
              </Button>
              <Button type="submit" className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-project-btn">
                {language === 'en' ? 'Save' : 'Simpan'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default ManageProjects;
